import React, { Key, useCallback, useMemo, useState } from "react";
import { useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { DataNode } from "antd/es/tree";
import { App, Button, Flex, Grid, Modal, Spin, Tree, Typography } from "antd";

import { PerfilService, useBuscarPerfil } from "@/Services/Sistema/PerfilService";

import { TypeMenu } from "@/Models/Menu";
import { Flatten, NoUndefinedState } from "@/utility-types";
import { decomporMenu } from "@/Utils/Menu";
import { PropriedadesDeNotificacao } from "@/Utils/Notificacoes";
import { UsuarioService } from "@/Services/UsuarioService";

import * as utilidadesDoMenu from "@/Utils/Menu";
import mitt from "mitt";

type NodeKey = DataNode["key"]

export type ControleDePermissoesType = {
    funcionalidadesSelecionadas: string[],
    menusSelecionados: string[]
};

const emitter = mitt<{invokeSalvar: void, salvo: void}>();

type ControleDePermissoesProps = {codPerfil: number, esconderAcoes?: boolean}

const expandirNodesRecursivamente = (arr: DataNode[]): DataNode[] => {
  let result: DataNode[] = [];

  function expandir(nodes: DataNode[]): void {
    for (const node of nodes) {
      result.push(node);
      if (node.children && node.children.length > 0) {
        expandir(node.children);
      }
    }
  }

  expandir(arr.slice());
  return result;
}

const obterChavesDosMenusComFolhasSelecionadas = (arr: DataNode[], chavesSelecionadas: Key[], anterior?: Set<Key>): Key[] => {
  let result: Set<Key> = anterior ?? new Set<Key>();

  for (const node of arr){
    if(node.isLeaf == true) continue;

    if(node.children && node.children.length > 0){
        if (expandirNodesRecursivamente(node.children).map((v) => v.key).some((k) => chavesSelecionadas.includes(k))) {
            result.add(node.key)
        }

        obterChavesDosMenusComFolhasSelecionadas(node.children, chavesSelecionadas, result )
    }
  }

  return Array.from(result);
}

const menuParaDataNode = (item: TypeMenu): DataNode => {
    let d: DataNode = {
        checkable: true,
        key: item.codigo,
        title: item.nome,
        isLeaf: false
    };

    if (item.menusFilhos != undefined && item.menusFilhos.length > 0) {
        d.children = item.menusFilhos.map((m) => menuParaDataNode(m))
    }

    if (item.funcionalidades.length > 0) {
        d.children = [...(d.children ?? []), ...(item.funcionalidades.map((f) => {
            let dn: DataNode = {
                checkable: true,
                key: f.nome,
                title: f.descricao ?? f.nome,
                isLeaf: true
            }

            return dn
        }))]
    }

    return d;
}

const obterDescendentes = (key: NodeKey, treeData: DataNode[]): null | NodeKey[] => {
    type Nodes = NoUndefinedState<DataNode["children"]>;
    type Node = Flatten<NoUndefinedState<DataNode["children"]>>

    const encontrarNodeAlvo = (nodes: Nodes): Node | null => {
        for (let node of nodes) {
            if (node.key == key) {
                return node;
            }

            if (node.children) {
                const found = encontrarNodeAlvo(node.children);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    };

    const encontrado = encontrarNodeAlvo(treeData);
    if(encontrado == null) return null

    let descendentes: NodeKey[] = [];
    const salvarDescendentesRecursivamente = (nodes: Nodes) => {
        nodes.forEach((n) => {
            descendentes.push(n.key);
            if (n.children) {
                salvarDescendentesRecursivamente(n.children);
            }
        });
    };

    salvarDescendentesRecursivamente(encontrado.children ?? []);
    return descendentes;
};

export const ControleDePermissoes = ({esconderAcoes, ...props}: ControleDePermissoesProps) => {

    const [status, setStatus] = useState<ControleDePermissoesType>({ funcionalidadesSelecionadas: [], menusSelecionados: [] });
    const [chavesSelecionadas, setChavesSelecionadas] = useState<React.Key[]>([]);

    const notification = App.useApp().notification;

    //#region arvore
    const { data: todosMenusDisponiveisData, ...todosMenusDisponiveisQuery } = useQuery({
        queryKey: ['listarTodosMenus'],
        queryFn: () => new UsuarioService().listarMenu(true).then((v) => utilidadesDoMenu.comporMenuHierarquico(v.data)),
        gcTime: Infinity,
        enabled: true
    });

    const dadosDaArvore = useMemo(() => {
        return todosMenusDisponiveisData?.map(menuParaDataNode)
    }, [todosMenusDisponiveisData])

    //#endregion

    //#region perfil
    const {perfil, query: perfilQuery} = useBuscarPerfil(props.codPerfil, props.codPerfil != undefined);

    useEffect(() => {
        if(perfil == null) return;

        let funcionalidadesPermitidas = (perfil.permissoes?? []).map((p) => {
            let k: Key = p.nome;
            return k;
        }) ?? [];

        let menusPermitidos = (perfil.menus?? []).map((m) => m.codigo) ?? [];

        setChavesSelecionadas([ ...funcionalidadesPermitidas, ...menusPermitidos ]);
        setStatus({
            funcionalidadesSelecionadas: funcionalidadesPermitidas,
            menusSelecionados: menusPermitidos
        })
    }, [perfil])

    useEffect(() => {
        /* As funcionalidades são marcadas com 'isLeaf: true'. Aqui é feito um filtro para se obter somente a chave desses itens. */

        if(dadosDaArvore == undefined){
            return
        }

        const menus = chavesSelecionadas.map((c) => c.toString()).filter((m) => isMenu(m.toString()));
        const funcionalidades = chavesSelecionadas.map((c) => c.toString()).filter(c => !menus.includes(c));

        setStatus({funcionalidadesSelecionadas: funcionalidades, menusSelecionados: menus})

    }, [chavesSelecionadas])

    //#endregion

    //#region funções auxiliares
    const menuDecomposto = useMemo(() => decomporMenu(todosMenusDisponiveisData ?? []), [todosMenusDisponiveisData]);
    const todasFuncionalidades = Array.from(new Set(menuDecomposto.flatMap((m) => m.funcionalidades).flatMap((m) => m.nome)));

    const isMenu = useCallback((key: string) => menuDecomposto.some((m) => m.codigo == key), [menuDecomposto]);

    const marcarTodosRecursivamente = (key: NodeKey) => {
        if(dadosDaArvore == undefined) return;
        const descendentes = obterDescendentes(key, dadosDaArvore) ?? [];

        const x = Array.from(new Set(chavesSelecionadas.concat(descendentes).concat([key])));
        setChavesSelecionadas(x);
    }

    const desmarcarTodosRecursivamente = (key: NodeKey) => {
        if(dadosDaArvore == undefined) return;
        const descendentes = obterDescendentes(key, dadosDaArvore) ?? [];

        const x = Array.from(new Set(descendentes.concat([key])));
        setChavesSelecionadas((prev) => prev.filter((k) => x.includes(k)? false : true ));
    }
    //#endregion

    const {mutate, ...mutation} = useMutation({
        mutationKey: ["Permissoes", "Permissoes.Atualizar", {codPerfil: perfil?.codigo, status}],
        mutationFn: ({ codPerfil, menus, funcionalidades }: { codPerfil: number, menus: string[], funcionalidades: string[] }) => {
            if (codPerfil == null) return Promise.reject();

            return Promise.all([
                PerfilService.atualizarMenus(codPerfil, menus),
                PerfilService.atualizarPermissoes(codPerfil, funcionalidades),
            ])
        },
        onSuccess: () => {
            notification.open({
                ...PropriedadesDeNotificacao.Sucesso(
                    <>
                        Permissões atualizadas com sucesso. <br />
                        As mudanças serão aplicadas em breve.
                    </>
                ),
            });

            emitter.emit("salvo");
        }
    })

    const salvar = () => {
        if(perfil?.codigo != undefined){
            mutate({ codPerfil: perfil.codigo, menus: status.menusSelecionados, funcionalidades: status.funcionalidadesSelecionadas });
        }
    }

    useEffect(() => {
        emitter.on("invokeSalvar", salvar);

        return () => {
            emitter.off("invokeSalvar", salvar);
        }
    }, [perfil, status])

    return (
        <React.Fragment>
            <Flex vertical gap={8}>
                <Flex justify="space-between" wrap="wrap">
                    <Typography.Text type="secondary" style={{ marginInline: "1em" }}>
                        <b>(M)</b>: Menu <b>(F)</b>: Funcionalidade
                    </Typography.Text>

                    <Typography.Text type="secondary" style={{ marginInline: "1em" }}>
                        <Typography.Text strong>
                            {status.menusSelecionados.length}/{menuDecomposto.length}{" "}
                        </Typography.Text>
                        menu(s),{" "}
                        <Typography.Text strong>
                            {status.funcionalidadesSelecionadas.length}/{todasFuncionalidades.length}{" "}
                        </Typography.Text>
                        funcionalidade(s)
                    </Typography.Text>
                </Flex>

                {perfil != null && (
                    <Tree
                        checkable
                        checkedKeys={chavesSelecionadas}
                        checkStrictly
                        titleRender={(node) => {
                            const ehMenu = isMenu(node.key.toString());
                            const title = typeof node.title == "function" ? node.title?.(node) : node.title;

                            if (ehMenu)
                                return (
                                    <Typography.Text>
                                        {title}{" "}
                                        <Typography.Text style={{ fontSize: ".75em" }} strong>
                                            (M)
                                        </Typography.Text>{" "}
                                        <span style={{ marginLeft: "1em" }}>
                                            <Button type="link" onClick={() => marcarTodosRecursivamente(node.key)} style={{ padding: 0 }}>
                                                Selecionar tudo
                                            </Button>

                                            <Button
                                                type="link"
                                                onClick={() => desmarcarTodosRecursivamente(node.key)}
                                                style={{ padding: 0, marginLeft: "1em" }}
                                            >
                                                Desmarcar tudo
                                            </Button>
                                        </span>
                                    </Typography.Text>
                                );
                            return (
                                <Typography.Text>
                                    {title}{" "}
                                    <Typography.Text style={{ fontSize: ".75em" }} strong>
                                        (F)
                                    </Typography.Text>{" "}
                                </Typography.Text>
                            );
                        }}
                        onCheck={(checked) => {
                            if (Array.isArray(checked)) {
                                setChavesSelecionadas(checked);
                            } else {
                                setChavesSelecionadas(checked.checked);
                            }
                        }}
                        treeData={dadosDaArvore}
                    />
                )}
            </Flex>
        </React.Fragment>
    );
}

type ControleDePermissoesModalProps = {
    codPerfil: number;
    modalProps?: React.ComponentProps<typeof Modal>;
    buttonOpen?: React.ReactNode;
}

export const ControleDePermissoesModal = ({ codPerfil, buttonOpen }: ControleDePermissoesModalProps) => {
    const breakpoint = Grid.useBreakpoint();

    const [aberta, setAberta] = useState(false);
    const [loading, setLoading] = useState(false);
    
    useEffect(() => {
        const handleSalvo = () => {
            setLoading(false);
            setAberta(false);
        }

        emitter.on("salvo", handleSalvo);

        return () => {
            emitter.off("salvo", handleSalvo);
        }
    }, [])

    const onOk = () => {
        setLoading(true);
        emitter.emit("invokeSalvar");
    }

    const onCancel = () => {
        setLoading(false);
        setAberta(false);
    }

    return (
        <React.Fragment>
            {(buttonOpen &&
                React.cloneElement(buttonOpen as React.ReactElement<any>, {
                    onClick: () => setAberta(true),
                })) || (
                <Button type="primary" size="small" onClick={() => setAberta(true)}>
                    Vincular Usuários
                </Button>
            )}

            <Spin spinning={loading}>
                <Modal
                    destroyOnClose
                    width={breakpoint.lg ? "60vw" : "80vw"}
                    styles={{ body: { overflowY: "auto", maxHeight: "calc(100vh - 200px)", scrollbarWidth: "thin" } }}
                    maskClosable={false}
                    open={aberta}
                    title={`Permissões`}
                    onCancel={onCancel}
                    onOk={onOk}
                    okButtonProps={{ loading }}
                >
                    <ControleDePermissoes codPerfil={codPerfil} esconderAcoes={true} />
                </Modal>
            </Spin>
        </React.Fragment>
    );
}